import type { NextPage } from "next";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import SharePurpleArea from "../../components/SharePurpleArea";
import {
  getAllPublicQuests,
  getMetadata,
  getQuestsPage,
  sharedServerSide,
  sortQuests,
} from "../../helpers";
import { loadCurrentpage } from "../../store/slices/app-slice";
import AvailableCard from "../../components/AvailableCard";
import { Locale } from "../../src/types";
import FilterTag from "../../components/FilterTabs";
import { IPresentationQuest, IQuestsPage } from "../../src/interfaces";
import QuestCard from "../../components/QuestCard";
import { useLoginContext } from "../../hooks";

enum NumberOfQuestsPerPage {
  SMALLEST = 1,
  SMALL = 4,
  MEDIUM = 9,
  LARGE = 12,
}

export interface QuestsProps {
  publicQuests: IPresentationQuest[];
  questsPage: IQuestsPage;
  serverDateJSON: Date;
}

export const getServerSideProps = sharedServerSide(async ({ locale }) => {
  const currentLocale = locale as Locale;
  const serverDateJSON = new Date().toJSON();
  const [publicQuests, questsPage, metas] = await Promise.all([
    getAllPublicQuests(currentLocale),
    getQuestsPage(currentLocale),
    getMetadata("quests", currentLocale),
  ]);

  return {
    props: {
      publicQuests: publicQuests.sort((a, b) =>
        sortQuests(a, b, new Date(serverDateJSON))
      ),
      questsPage,
      serverDateJSON,
      metas,
      ...(await serverSideTranslations(currentLocale, ["common", "quests"])),
    },
  };
});

const Quests: NextPage<QuestsProps> = function ({
  publicQuests,
  questsPage,
  serverDateJSON,
}) {
  const { t } = useTranslation("quests");
  const { t: tCommon } = useTranslation("common");
  const serverDate = new Date(serverDateJSON);
  const dispatch = useDispatch();
  const router = useRouter();
  const { isReadyToQuest, userCompletedQuestsAsynchronous } = useLoginContext();
  const [questsWithCompletion, setQuestsWithCompletion] =
    useState<IPresentationQuest[]>(publicQuests);
  const [categoryOption, setCategoryOption] = useState<number>(0);
  const [questFiltered, setQuestFiltered] = useState<IPresentationQuest[]>([]);
  const [page, setPage] = useState<number>(1);
  const [perPage, SetPerPage] = useState<number>(NumberOfQuestsPerPage.MEDIUM);
  const [count, setCount] = useState<number>(0);
  const [displayedQuests, setDisplayedQuests] = useState<IPresentationQuest[]>(
    []
  );
  // optionsState defines the state of the filter options initiated to false for all options
  const [optionsState, setOptionsState] = useState<boolean[]>(
    Array(8).fill(false)
  );

  // Filter options
  const filterOptions = [
    "community",
    "academy",
    "partner",
    "available",
    "coming-soon",
    "ended",
    "todo",
    "completed",
  ];
  // functions to filter the correct quests according to the selected status
  const filterFonctions = {
    available: (item: IPresentationQuest) =>
      serverDate > new Date(item.startDate) &&
      (serverDate < new Date(item.endDate) || !item.endDate),
    "coming-soon": (item: IPresentationQuest) =>
      serverDate < new Date(item.startDate),
    "quest-ended": (item: IPresentationQuest) =>
      serverDate > new Date(item.endDate) && item.endDate,
    todo: (item: IPresentationQuest) =>
      !item.completed &&
      serverDate > new Date(item.startDate) &&
      (serverDate < new Date(item.endDate) || !item.endDate),
    completed: (item: IPresentationQuest) => !!item.completed,
  };
  const isSmall = useMediaQuery("(max-width: 425px)");
  const isMd = useMediaQuery("(max-width: 768px)");
  const isLg = useMediaQuery("(max-width: 1280px)");
  const handleChange = (e, p) => {
    setPage(p);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const getDateDifference = (a: IPresentationQuest, b: IPresentationQuest) =>
    new Date(b.startDate).getTime() - new Date(a.startDate).getTime();

  const questFilterd = () => {
    setQuestFiltered([]);
    let tempQuestCategory = [];
    // if no category selected
    if (categoryOption === 0) tempQuestCategory = questsWithCompletion;
    else {
      const selectedCategory = filterOptions[categoryOption - 1];
      tempQuestCategory = questsWithCompletion.filter(
        (item) => item.categoryId.toLowerCase() === selectedCategory
      );
    }

    let tempQuestStatus = [];
    let statusOptionSelected = true;
    //  if no status option is selected we concat all the statusQuests (sorted and grouped by status)
    if (!optionsState[3] && !optionsState[4] && !optionsState[5]) {
      statusOptionSelected = false;
    }
    // if quest status is Available
    if (optionsState[3] || !statusOptionSelected)
      tempQuestStatus = tempQuestStatus.concat(
        tempQuestCategory
          .filter(filterFonctions.available)
          .sort((a, b) => sortQuests(a, b, serverDate))
      );

    // if quest status is Coming-soon
    if (optionsState[4] || !statusOptionSelected)
      tempQuestStatus = tempQuestStatus.concat(
        tempQuestCategory
          .filter(filterFonctions["coming-soon"])
          .sort((a: IPresentationQuest, b: IPresentationQuest) =>
            getDateDifference(b, a)
          )
      );
    // if quest status is Quest-ended
    if (optionsState[5] || !statusOptionSelected)
      tempQuestStatus = tempQuestStatus.concat(
        tempQuestCategory
          .filter(filterFonctions["quest-ended"])
          .sort((a: IPresentationQuest, b: IPresentationQuest) =>
            getDateDifference(a, b)
          )
      );

    let tempQuestCompletion = tempQuestStatus;

    if (isReadyToQuest) {
      if (optionsState[6]) {
        tempQuestCompletion = tempQuestCompletion
          .filter(filterFonctions.todo)
          .sort((a, b) => sortQuests(a, b, serverDate));
      } else if (optionsState[7]) {
        tempQuestCompletion = tempQuestCompletion
          .filter(filterFonctions.completed)
          .sort((a, b) => sortQuests(a, b, serverDate));
      }
    }

    setQuestFiltered([...tempQuestCompletion]);
  };

  useEffect(() => {
    if (isMd) SetPerPage(NumberOfQuestsPerPage.LARGE);
    if (isLg) SetPerPage(NumberOfQuestsPerPage.MEDIUM);
    if (isSmall) SetPerPage(NumberOfQuestsPerPage.SMALL);
  }, [isMd, isLg, isSmall]);

  useEffect(() => {
    setCount(Math.ceil(questFiltered.length / perPage));
    setDisplayedQuests(
      questFiltered.slice(
        (page - 1) * perPage,
        page * perPage < questFiltered.length
          ? page * perPage
          : questFiltered.length
      )
    );
  }, [perPage, questFiltered, page]);

  useEffect(() => {
    dispatch(loadCurrentpage("Quests"));
    const tempOption: boolean[] = optionsState;
    router.query.status
      ?.toString()
      .split("_")
      .forEach((item) => {
        tempOption[filterOptions.indexOf(item)] = true;
      });
    if (router.query.category?.toString()) {
      setCategoryOption(
        filterOptions.indexOf(router.query.category?.toString()) + 1
      );
    } else {
      setCategoryOption(0);
    }

    setOptionsState([...tempOption]);
    questFilterd();
  }, []);

  useEffect(() => {
    const tempOption: boolean[] = optionsState;
    tempOption.fill(false, 6, 8);
    setOptionsState([...tempOption]);

    if (isReadyToQuest) {
      setQuestsWithCompletion(
        publicQuests.map((quest) => ({
          ...quest,
          completed: userCompletedQuestsAsynchronous.includes(
            Number(quest.collectionId)
          ),
        }))
      );
    } else {
      setQuestsWithCompletion(
        publicQuests.map((quest) => ({ ...quest, completed: null }))
      );
    }
  }, [isReadyToQuest, userCompletedQuestsAsynchronous]);

  const handleClickStatus = (e) => {
    const tempOption: boolean[] = optionsState;
    const selectedOption: number = filterOptions.indexOf(e.target.id);
    // make the options exlusive by setting to false the three status filter options
    if (!tempOption[selectedOption]) tempOption.fill(false, 3, 6);
    // update the state of the selected option
    tempOption[selectedOption] = !optionsState[selectedOption];
    setOptionsState([...tempOption]);
  };

  const handleClickCategory = (e) => {
    const indexOfselectedCategory = filterOptions.indexOf(e.target.id) + 1;
    // set the category option to the selected one
    setCategoryOption(
      indexOfselectedCategory === categoryOption ? 0 : indexOfselectedCategory
    );
  };

  const handleClickCompletion = (e) => {
    const tempOption: boolean[] = optionsState;
    const selectedOption: number = filterOptions.indexOf(e.target.id);
    // make the options exlusive by setting to false the three status filter options
    if (!tempOption[selectedOption]) tempOption.fill(false, 6, 8);
    // update the state of the selected option
    tempOption[selectedOption] = !optionsState[selectedOption];
    setOptionsState([...tempOption]);
  };

  const updateUrl = () => {
    const category: string =
      categoryOption === 0 ? "" : filterOptions[categoryOption - 1];

    let status: string;
    let completion: string;
    optionsState.forEach((item, i) => {
      if (item === true && i > 2) {
        if (i < 6) {
          status = filterOptions[i];
        } else {
          completion = filterOptions[i];
        }
      }
    });
    if (isReadyToQuest) {
      router.replace(
        {
          query: {
            category: category,
            status: status,
            completion: completion,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(
        {
          query: {
            category: category,
            status: status,
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  useEffect(() => {
    setPage(1);
    updateUrl();
    questFilterd();
  }, [categoryOption, optionsState, questsWithCompletion]);

  return (
    <div className="w-full bg-white">
      <div className="w-full md:h-[439px] h-[483px] object-cover absolute">
        <div className="w-full h-[100%] bg-[#270602]">
          <video
            className="object-cover w-full h-[100%]"
            playsInline
            autoPlay
            muted
            loop
            poster=""
          >
            <source
              src="/videos/quest-background-compress.mp4"
              type="video/mp4"
            />
          </video>
        </div>
      </div>
      <div className="max-w-[1440px] mx-auto w-full md:h-[439px] h-[483px] md:pt-[168px] pt-[137px] xl:px-[114px] md:px-[50px] px-4  relative">
        <div className="text-white display-4 md:display-3 uppercase">
          {questsPage.heroTitle}
        </div>
        <div className="md:mt-6 lg:max-w-[571px] md:max-w-[360px] text-white bodyText mt-10">
          {questsPage.heroDescription}
        </div>
        <div className="md:mt-6 lg:max-w-[571px] md:max-w-[360px] text-white bodyText md:flex hidden">
          {t("tagline")}
          <button
            type="button"
            onClick={() => router.push(`/faq#content9`)}
            className="border-b cursor-pointer hover:border-transparent ml-1"
          >
            {tCommon("learnMore")}
          </button>
        </div>
        <div className="absolute right-0 bottom-0 md:w-auto w-full md:block hidden">
          <AvailableCard
            counts={
              questsWithCompletion.filter(
                (item) =>
                  serverDate > new Date(item.startDate) &&
                  (serverDate < new Date(item.endDate) || !item.endDate)
              ).length || 0
            }
          />
        </div>
      </div>
      <div className=" w-full md:hidden">
        <AvailableCard
          counts={
            questsWithCompletion.filter(
              (item) =>
                serverDate > new Date(item.startDate) &&
                (serverDate < new Date(item.endDate) || !item.endDate)
            ).length || 0
          }
        />
      </div>
      {/* Filter option */}
      <div className="max-w-[1440px] w-full bg-white py-10 xl:px-[112px] md:px-[50px] px-4 flex gap-x-[145px] gap-y-[32px] mx-auto flex-wrap">
        <div>
          <span className="highlightText">{t("questCategory")}</span>
          <div className="flex flex-wrap gap-2 mt-3">
            <FilterTag
              label={tCommon("categories.community")}
              active={categoryOption === 1}
              onClick={handleClickCategory}
              id="community"
            />
            <FilterTag
              label={tCommon("categories.academy")}
              active={categoryOption === 2}
              onClick={handleClickCategory}
              id="academy"
            />
            <FilterTag
              label={tCommon("categories.partner")}
              active={categoryOption === 3}
              onClick={handleClickCategory}
              id="partner"
            />
          </div>
        </div>
        <div>
          <span className="highlightText">{t("questStatus")}</span>
          <div className="flex flex-wrap gap-2 mt-3 text-white">
            <FilterTag
              label={tCommon("status.available")}
              active={optionsState[3]}
              onClick={handleClickStatus}
              id="available"
              filterOption="status"
            />
            <FilterTag
              label={tCommon("status.comingSoon")}
              active={optionsState[4]}
              onClick={handleClickStatus}
              id="coming-soon"
              filterOption="status"
            />
            <FilterTag
              label={tCommon("status.questEnded")}
              active={optionsState[5]}
              onClick={handleClickStatus}
              id="ended"
              filterOption="status"
            />
          </div>
        </div>
        {isReadyToQuest ? (
          <div>
            <span className="highlightText">{t("questCompletion")}</span>
            <div className="flex flex-wrap gap-2 mt-3 text-white">
              <FilterTag
                label={tCommon("status.todo")}
                active={optionsState[6]}
                onClick={handleClickCompletion}
                id="todo"
                filterOption="completion"
              />
              <FilterTag
                label={tCommon("status.completed")}
                active={optionsState[7]}
                onClick={handleClickCompletion}
                id="completed"
                filterOption="completion"
              />
            </div>
          </div>
        ) : null}
      </div>
      <div className="max-w-[1440px] xl:px-[112px] md:px-[50px] px-4 w-full mx-auto">
        <div className="border border-light-gray" />
      </div>
      <div className="max-w-[1440px] w-full xl:px-[112px] md:px-[50px] px-4 mt-[24px] mx-auto pb-[120px]">
        <div className="highlightText mb-10">
          {tCommon("questsWithCount", { count: questFiltered.length })}
        </div>
        <div className="flex">
          <div className="flex flex-wrap gap-x-[32px] gap-y-7 md:gap-y-14">
            {displayedQuests.map((quest) => (
              <QuestCard key={quest.id} quest={quest} now={serverDate} />
            ))}
          </div>
        </div>
        <Pagination
          count={count}
          page={page}
          size={isSmall ? "small" : "large"}
          siblingCount={isSmall ? 0 : 2}
          onChange={handleChange}
          className="mt-[80px] flex justify-center"
        />
      </div>
      <SharePurpleArea />
    </div>
  );
};

export default Quests;
